import Overlay from 'mini-overlay/lib/components/Overlay';
import React, { useEffect, useRef } from 'react';
import strings from 'owa-strings/owa-mini-strings';
import { AccessibilityAnchorButton } from 'mini-accessibility';
import { BottomSheetAnimatedProps } from '../schema/BottomSheetAnimatedProps';
import { escapeKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { getBottomSheetAnimatedDivId } from '../utils/bottomSheetAnimatedDivUtils';
import { mergeOverrideStyles } from 'mini-sass-mixins/lib/utils/mergeOverrideStyles';
import { TransitionEffects, TransitionStage } from 'mini-transition-stage';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import { useApplySafeAreaPadding } from 'mini-hooks/lib/useApplySafeAreaPadding';
import { useFocusTrap } from 'focus-trap-zone';
import { usePrevious } from 'mini-hooks/lib/usePrevious';
import { useWindowDimensions } from 'mini-hooks/lib/useWindowDimensions';

const styles = require('./BottomSheetAnimated.scss');
const classNames = require('classnames/bind').bind(styles);

let darkOverlayCreator: string = null;

enum RenderState {
    Open = 1,
    Ducked,
    Closed,
}

export const BottomSheetAnimated: React.FC<BottomSheetAnimatedProps> = React.forwardRef(
    (props, ref?: React.RefObject<any>) => {
        const {
            id,
            open,
            ducked,
            elementToFocusOnOpen,
            elementToFocusOnClose,
            onUnmounted,
            children,
            onOverlayClick,
            addAccessibleCloseButton = true,
            noSafeArea = false,
            containerStyles = '',
        } = props;

        const isDarkOverlayCreator = !darkOverlayCreator || darkOverlayCreator === id;
        // A bottom sheet has the authority to create the dark overlay if there is no darkOverlay creator already or
        // if there is a dark overlay creator whose id is same as the id of this bottom sheet
        const acquireDarkOverlayCreationRights = () =>
            !darkOverlayCreator && (darkOverlayCreator = id);
        const tryReleasingDarkOverlayCreationRights = () =>
            isDarkOverlayCreator && (darkOverlayCreator = null);

        let renderState: RenderState = RenderState.Open;

        if (!open) {
            renderState = RenderState.Closed;
        } else if (ducked) {
            renderState = RenderState.Ducked;
        }

        const shallowCleanup = () => {
            /* Trigger the unmount unless ducking the bottomsheet for navigation. */
            if (renderState === RenderState.Closed) {
                tryReleasingDarkOverlayCreationRights();
                /**
                 * invoke the onUnmount callback which should take care of removing the component.
                 */
                onUnmounted && onUnmounted();
            }
        };

        const currentRenderState: RenderState = renderState;
        const previousRenderState: RenderState = usePrevious<RenderState>(
            renderState,
            RenderState.Closed
        );

        const additionalContainerRef = useRef<any>(null);
        const contentRef = ref ?? additionalContainerRef;
        useApplySafeAreaPadding(contentRef, noSafeArea);

        // get the focus trap related stuff
        const focusTrapZoneId: string = getBottomSheetAnimatedDivId(id);
        const { createFocusTrap, relinquishFocusTrap } = useFocusTrap(focusTrapZoneId);

        useEffect(() => {
            const onEscapeKeyHandler = escapeKeyHandler(e => {
                e.preventDefault();
                onOverlayClick(e);
            });
            document.addEventListener('keydown', onEscapeKeyHandler, true);

            return () => {
                document.removeEventListener('keydown', onEscapeKeyHandler, true);
            };
        }, []);

        // Component did update
        useEffect(() => {
            if (currentRenderState !== previousRenderState) {
                // Only if the render state has changed do we need these operations

                switch (currentRenderState) {
                    case RenderState.Open:
                        switch (previousRenderState) {
                            case RenderState.Ducked:
                                // In this case we just need to create a focus trap, restoration of the previous divs will happen only on close and that too with the values present on opening from closed state
                                createFocusTrap();
                                break;
                            case RenderState.Closed:
                            default:
                                // If we do not have the overlay owner, then this bottom sheet is the first one to open and becomes the overlay owner
                                acquireDarkOverlayCreationRights();
                                // This seems to be a completely new bottom sheet opening, hence create a focus trap and focus into the correct element
                                createFocusTrap();
                                /**
                                 * This is done to make sure BottomSheetAnimated get focused last,
                                 * therefore overriding any other focused element in background.
                                 */
                                setTimeout(() => trySetFocusOnElement(elementToFocusOnOpen), 0);
                                break;
                        }
                        break;
                    case RenderState.Closed:
                        // Moving from open/ducked to closed state, hence relinquish the focus trap and also try setting focus on the element which needs to be focused
                        relinquishFocusTrap();
                        setTimeout(() => {
                            trySetFocusOnElement(elementToFocusOnClose);
                        }, 0);
                        break;
                }
            }
        });

        /* We need to release overlay creation rights ASAP on close. Otherwise, if there's
         * another bottomsheet coming up, chances are it will be rendered without an overlay.
         */
        if (renderState === RenderState.Closed) {
            tryReleasingDarkOverlayCreationRights();
        }

        const overlayClassnames = classNames(
            styles.bottomSheetAnimatedBackground,
            /* Paint the overlay transparent if it is not the first one on screen. */
            darkOverlayCreator &&
                !isDarkOverlayCreator &&
                styles.overlappingBottomSheetAnimatedBackground
        );

        useEffect(() => tryReleasingDarkOverlayCreationRights, [isDarkOverlayCreator]);
        const windowHeight = useWindowDimensions().height;
        const maxHeightForBottomSheetAnimated = windowHeight - 44 + 'px';

        return children ? (
            <>
                <TransitionStage
                    /* Show overlay if open or ducked, don't show if closed.
                     * Overlay remains even if sheet is ducked, to avoid flicker while
                     * transitioning in and out of spokes.
                     */
                    open={renderState !== RenderState.Closed}
                    inEffect={TransitionEffects.fadeIn}
                    outEffect={TransitionEffects.fadeOut}>
                    <Overlay onClick={onOverlayClick} className={overlayClassnames} />
                </TransitionStage>
                <TransitionStage
                    /* Show sheet only if open, don't show if ducked or closed */
                    open={renderState === RenderState.Open}
                    /* Unmount sheet only if closed, don't unmount if open or ducked */
                    unmountOnExit={renderState === RenderState.Closed}
                    onUnmounted={shallowCleanup}>
                    <div className={styles.bottomSheetAnimated}>
                        <div className={styles.bottomSheetAnimatedOuterContainer}>
                            <div
                                ref={contentRef}
                                className={classNames(
                                    styles.bottomSheetAnimatedContainer,
                                    containerStyles,
                                    mergeOverrideStyles({
                                        maxHeight: maxHeightForBottomSheetAnimated,
                                    })
                                )}>
                                {children}
                            </div>
                        </div>
                        {addAccessibleCloseButton && (
                            <AccessibilityAnchorButton
                                label={strings.closeDialog}
                                onClick={onOverlayClick}
                            />
                        )}
                    </div>
                </TransitionStage>
            </>
        ) : null;
    }
);

export default BottomSheetAnimated;
